import { ReactNode, useEffect, useRef, useState } from 'react';

interface CrossFadeProps {
  /** Stable identifier for the current child. When this changes, the
   * outgoing children are crossfaded to the new ones. */
  variantKey: string;
  /** Transition duration in milliseconds. */
  duration?: number;
  className?: string;
  children: ReactNode;
}

const DEFAULT_DURATION = 200;

/**
 * Crossfades between two sets of children when `variantKey` changes.
 * Honours `prefers-reduced-motion` by switching instantly when the user
 * has expressed that preference.
 */
function CrossFade({ variantKey, duration = DEFAULT_DURATION, className, children }: CrossFadeProps) {
  const [activeKey, setActiveKey] = useState(variantKey);
  const [activeChildren, setActiveChildren] = useState<ReactNode>(children);
  const [phase, setPhase] = useState<'idle' | 'fading'>('idle');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const childrenRef = useRef<ReactNode>(children);

  // Keep the ref up to date so the timeout callback always sees the latest
  // children even if the parent re-renders mid-fade. We deliberately do not
  // depend on `children` in the transition effect below — that would restart
  // the fade timer on every parent render.
  childrenRef.current = children;

  // When no transition is in flight, sync the visible children with the
  // latest `children` prop. This effect is cheap: it short-circuits unless
  // the visible tree is the active variant.
  useEffect(() => {
    if (variantKey === activeKey && phase === 'idle') {
      setActiveChildren(children);
    }
  }, [children, variantKey, activeKey, phase]);

  useEffect(() => {
    // Same variant — guarantee we end up visible. This handles the
    // "A → B → A mid-fade" sequence where the cleanup clears the in-flight
    // timeout but `phase` would otherwise stay at `'fading'` forever.
    if (variantKey === activeKey) {
      setPhase('idle');
      return;
    }

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setActiveKey(variantKey);
      setActiveChildren(childrenRef.current);
      setPhase('idle');
      return;
    }

    setPhase('fading');
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setActiveKey(variantKey);
      setActiveChildren(childrenRef.current);
      setPhase('idle');
    }, duration);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [variantKey, activeKey, duration]);

  const isFading = phase === 'fading';
  const style = {
    transition: `opacity ${duration}ms ease`,
    opacity: isFading ? 0 : 1,
  };

  const wrapperClassName = `cross-fade${className ? ` ${className}` : ''}`;

  // While fading, mark the subtree inert so the invisible (outgoing) content
  // is removed from focus order and the accessibility tree atomically.
  // Spread the attribute so we can keep React 18 typings happy without casts.
  const inertProps = isFading ? { inert: '', 'aria-hidden': true as const } : {};

  return (
    <div className={wrapperClassName} style={style} data-cross-fade-key={activeKey} {...inertProps}>
      {activeChildren}
    </div>
  );
}

export default CrossFade;
